import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Dialog, IconButton, Pagination, Skeleton, Typography } from "@mui/material";
import { QueryObserverResult, RefetchOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { colors } from "../../../theme/palette";
import { getEventJoinRequests, removeJoiningPlayersFromEvent } from "../../../api/events";
import { CloseDialogButtonIcon, EventPlayerInviteIcon } from "../../../assets/icons/MUI/EventsIcons";

import FindPlayersFigure from "./FindPlayersFigure";
import JoiningPlayersList from "./JoiningPlayersList";
import RequestsList from "./RequestsList";

const Guests = ({
  eventId,
  totalPlayersInvitedNum,
  acceptedPlayersNum,
  isOwnEvent,
  isCanceled,
  status,
  isEventDetailsLoading,
  refetchEvent,
}: {
  eventId?: string;
  totalPlayersInvitedNum: number;
  acceptedPlayersNum: number;
  isOwnEvent: boolean;
  isCanceled: boolean;
  status: string | undefined;
  isEventDetailsLoading: boolean;
  refetchEvent: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}) => {
  const { t } = useTranslation();
  const [activeGuests, setActiveGuests] = useState("JOINED");
  const [playerName, setPlayerName] = useState("");
  const [totalJoinNum, setTotalJoinNum] = useState(acceptedPlayersNum);
  const [playerId, setPlayerId] = useState<null | number>(null);
  const [requestsPage, setRequestsPage] = useState<number>(1);
  const [openDialog, setOpenDialog] = useState(false);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location = useLocation();

  const resetDialogData = () => {
    setOpenDialog(false);
    setPlayerName("");
    setPlayerId(null);
  };

  const handleRemovePlayerFromEvent = async () => {
    try {
      await removeJoiningPlayersFromEvent(eventId, `${playerId}`);
      queryClient.invalidateQueries({ queryKey: ["joiningPlayersList"] });
      setTotalJoinNum((prev) => prev - 1);

      resetDialogData();
    } catch (error) {
      resetDialogData();
    }
  };

  const {
    data: eventJoinRequestsResult,
    isLoading: isEventJoinRequestsResultLoading,
    refetch,
  } = useQuery({
    queryKey: ["eventJoinRequests", eventId, requestsPage],
    queryFn: () => getEventJoinRequests(eventId || null, requestsPage),
  });

  const requestsList = eventJoinRequestsResult?.items || [];
  const requestsListMeta = eventJoinRequestsResult?.meta || {};

  const isGuestsLoading = isEventDetailsLoading || isEventJoinRequestsResultLoading;

  return (
    <Box
      sx={{
        maxWidth: "397px",
        width: "47%",
        background: "#F7F8F8",
        padding: "25px",
        borderRadius: "16px",
        minHeight: "60vh",
        maxHeight: "777px",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        {isGuestsLoading ? (
          <Skeleton variant="text" width="56px" height="20px" animation="wave" />
        ) : (
          <>
            <Typography variant="body16ExtraBold" sx={{ color: colors.Dark1000 }}>
              {isOwnEvent ? t("pages.events.eventDetails.guests") : t("pages.events.eventDetails.participants")}
            </Typography>
            {isOwnEvent && (
              <Button
                variant="text-sm"
                startIcon={<EventPlayerInviteIcon />}
                disabled={isCanceled || status === "FINISHED"}
                onClick={() => {
                  const invitePath = `${location.pathname}/invite`;
                  navigate(invitePath);
                }}
                sx={{
                  fontFamily: "HeadingNowVarTrial",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "24px",
                }}
              >
                {t("pages.events.eventDetails.invitePlayers")}
              </Button>
            )}
          </>
        )}
      </Box>
      {isGuestsLoading ? (
        <Box
          sx={{
            display: "flex",
            columnGap: "10px",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <Skeleton variant="text" width="115px" height="27px" animation="wave" />
          <Skeleton variant="text" width="115px" height="27px" animation="wave" />
        </Box>
      ) : (
        isOwnEvent && (
          <Box
            sx={{
              display: "flex",
              columnGap: "10px",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <Chip
              label={`${t("pages.events.eventDetails.joined")} (${totalJoinNum}/${totalPlayersInvitedNum})`}
              component="button"
              variant="outlined"
              clickable
              onClick={() => setActiveGuests("JOINED")}
              sx={{
                border: "none",
                padding: "1px 4px",
                color: activeGuests === "JOINED" ? colors.Green100 : colors.Dark600,
                background: activeGuests === "JOINED" ? colors.Green25 : "transparent",
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${t("pages.events.eventDetails.requests")} (${requestsListMeta?.totalItems || 0})`}
              component="button"
              variant="outlined"
              clickable
              onClick={() => setActiveGuests("REQUESTS")}
              sx={{
                border: "none",
                padding: "1px 4px",
                color: activeGuests === "REQUESTS" ? colors.Green100 : colors.Dark600,
                background: activeGuests === "REQUESTS" ? colors.Green25 : "transparent",
                fontWeight: 600,
              }}
            />
          </Box>
        )
      )}

      {isGuestsLoading ? (
        [...Array(4)].map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              maxHeight: "100%",
              overflow: "auto",
              columnGap: "20px",
              paddingBottom: "16px",
              marginBottom: idx === 3 ? undefined : "16px",
              borderBottom: idx === 3 ? undefined : "1px solid #E7E7E7",
            }}
          >
            <Skeleton variant="circular" width={47} height={47} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "fit-content",
              }}
            >
              <Skeleton variant="text" width="100px" height={24} />
              <Skeleton variant="text" width="160px" height={24} />
              <Skeleton variant="text" width="122px" height={24} />
            </Box>
          </Box>
        ))
      ) : (
        <></>
      )}
      {activeGuests === "JOINED" &&
        !isGuestsLoading &&
        (acceptedPlayersNum === 0 ? (
          <FindPlayersFigure isOwnEvent={isOwnEvent} isCanceled={isCanceled || status === "FINISHED"} />
        ) : (
          <JoiningPlayersList
            eventId={eventId}
            setPlayerId={setPlayerId}
            setPlayerName={setPlayerName}
            setOpenDialog={setOpenDialog}
            isOwnEvent={isOwnEvent}
          />
        ))}
      {activeGuests === "REQUESTS" &&
        !isGuestsLoading &&
        (requestsList.length === 0 ? (
          <FindPlayersFigure
            isOwnEvent={isOwnEvent}
            isRequests={isGuestsLoading}
            isCanceled={isCanceled || status === "FINISHED"}
          />
        ) : (
          <>
            <RequestsList
              isOwnEvent={isOwnEvent}
              requestsList={requestsList}
              eventId={eventId}
              refetch={() => {
                refetch();
                refetchEvent();
              }}
            />
            {requestsListMeta.totalItems > 5 && (
              <Pagination
                count={Math.ceil(requestsListMeta?.totalItems / 5) || 0}
                page={requestsPage}
                onChange={(event: React.ChangeEvent<unknown>, value: number) => {
                  setRequestsPage(value);
                }}
                sx={{
                  width: "100%",
                  margin: "25px auto 0",
                  direction: "ltr !important",
                  "& .MuiPagination-ul": {
                    justifyContent: "center",
                  },
                  "& .MuiButtonBase-root": {
                    borderRadius: "8px !important",
                  },
                }}
              />
            )}
          </>
        ))}
      <ConfirmRemovingPlayersDialog
        openDialog={openDialog}
        handleCloseDialog={() => setOpenDialog(false)}
        handleRemove={handleRemovePlayerFromEvent}
        playerName={playerName}
      />
    </Box>
  );
};

export default Guests;

const ConfirmRemovingPlayersDialog = ({
  openDialog,
  handleCloseDialog,
  handleRemove,
  playerName,
}: {
  openDialog: boolean;
  handleCloseDialog: () => void;
  handleRemove: () => void;
  playerName: string;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          width: "400px",
        },
      }}
    >
      <Typography
        variant="body16Bold"
        sx={{
          color: colors.Dark1000,
          display: "block",
          padding: "20px 0",
          textAlign: "center",
          borderBottom: "1px solid #EAECF0",
          position: "relative",
        }}
      >
        {t("pages.events.eventDetails.confirmationRequired")}
        <IconButton
          sx={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseDialogButtonIcon />
        </IconButton>
      </Typography>

      <Typography
        variant="body16Regular"
        sx={{
          color: colors.Dark600,
          display: "block",
          padding: "40px 24px",
          borderBottom: "1px solid #EAECF0",
        }}
      >
        {t("pages.events.eventDetails.areYouSureYouWantToRemove")}
        <Typography variant="body16Regular" sx={{ color: colors.Black100 }}>
          {playerName}
        </Typography>{" "}
        {t("pages.events.eventDetails.fromThePlayers")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          columnGap: "12px",
          padding: "24px",
        }}
      >
        <Button
          variant="outlined-sm"
          onClick={handleCloseDialog}
          sx={{
            width: "50%",
          }}
        >
          {t("pages.events.eventDetails.cancel")}
        </Button>
        <Button
          variant="primary-sm"
          onClick={handleRemove}
          sx={{
            width: "50%",
            background: colors.Red200,
            "&:hover": {
              background: colors.Red50,
            },
          }}
        >
          {t("pages.events.eventDetails.remove")}
        </Button>
      </Box>
    </Dialog>
  );
};
